import React from "react";
import {
  ExternalLink,
  ArrowRight,
  CheckCircle2,
  ShieldCheck,
  MapPin,
  Smartphone,
  Boxes,
  Activity,
} from "lucide-react";
import { IvManageMark, ZentrakMark } from "./ProductMarks";

interface ProductShowcaseProps {
  onSelectZentrakInquiry?: () => void;
}

export const ProductShowcase: React.FC<ProductShowcaseProps> = ({
  onSelectZentrakInquiry,
}) => {
  return (
    <section
      id="products"
      className="py-20 lg:py-28 bg-cream-100/50 border-b border-charcoal/5"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Heading */}
        <div className="max-w-3xl mx-auto text-center space-y-4 mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cream-300 text-xs font-mono font-medium text-charcoal-700 uppercase tracking-widest">
            Flagship Product Suite
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-ink tracking-tight">
            Purpose-Built Operating Systems for Field Operations
          </h2>
          <p className="text-base sm:text-lg text-ink-secondary leading-relaxed">
            Independent, dedicated platforms architected to replace fragmented
            spreadsheets, unverified WhatsApp check-ins, and opaque inventory
            movement.
          </p>
        </div>

        {/* 2 Flagship Product Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
          {/* Card 1: ivManage */}
          <div className="flex flex-col justify-between rounded-3xl bg-white p-8 sm:p-10 border border-charcoal/10 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden group">
            {/* Top Accent Bar */}
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-plum" />

            <div>
              {/* Header with Status Badge */}
              <div className="flex items-center justify-between gap-4 pb-6 border-b border-charcoal/5">
                <div className="flex items-center gap-3.5">
                  <div className="p-2.5 rounded-2xl bg-plum/10 text-plum group-hover:scale-105 transition-transform">
                    <IvManageMark size={36} color="#50404D" />
                  </div>
                  <div>
                    <h3 className="font-brand text-2xl sm:text-3xl font-bold text-ink tracking-tight">
                      ivManage
                    </h3>
                    <p className="text-xs font-mono text-plum uppercase tracking-wider font-semibold">
                      Distribution & Inventory OS
                    </p>
                  </div>
                </div>

                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-mono font-semibold">
                  <span className="w-2 h-2 rounded-full bg-emerald-600 animate-pulse" />
                  LIVE
                </div>
              </div>

              {/* Product Pitch */}
              <div className="pt-6 space-y-4">
                <p className="text-base text-ink leading-relaxed font-medium">
                  The complete operating system for multi-tier distribution
                  companies and regional FMCG brands.
                </p>
                <p className="text-sm text-ink-secondary leading-relaxed">
                  Connects warehouses, stockists, and field sales teams into a
                  single source of truth. Enables leadership to reconcile stock
                  movements in seconds and track actual field execution.
                </p>

                {/* Key Capabilities */}
                <div className="pt-4 space-y-3">
                  <h4 className="text-xs font-mono uppercase tracking-wider text-ink-muted">
                    Key Capabilities
                  </h4>
                  <ul className="space-y-2.5 text-sm text-ink-secondary">
                    <li className="flex items-start gap-2.5">
                      <Boxes className="w-4 h-4 text-plum shrink-0 mt-0.5" />
                      <span>
                        <strong className="text-ink font-semibold">
                          4-Tier Stock Reconciliation:
                        </strong>{" "}
                        Complete inventory movement audit across Warehouse →
                        Stockist → Salesperson → Retailer.
                      </span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <MapPin className="w-4 h-4 text-plum shrink-0 mt-0.5" />
                      <span>
                        <strong className="text-ink font-semibold">
                          GPS-Verified Rep Visits:
                        </strong>{" "}
                        Actual travel trace and adhoc retailer call logging with
                        zero falsified check-ins.
                      </span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <Activity className="w-4 h-4 text-plum shrink-0 mt-0.5" />
                      <span>
                        <strong className="text-ink font-semibold">
                          Trade Schemes & Combos:
                        </strong>{" "}
                        Dynamic buy-X-get-gift bundles and cumulative retailer
                        target schemes announced via SMS.
                      </span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <ShieldCheck className="w-4 h-4 text-plum shrink-0 mt-0.5" />
                      <span>
                        <strong className="text-ink font-semibold">
                          Enterprise Isolation:
                        </strong>{" "}
                        Strict company-level DB partitioning and reviewable
                        audit events.
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Card CTA */}
            <div className="pt-8 mt-8 border-t border-charcoal/5 flex flex-col sm:flex-row items-center justify-between gap-4">
              <span className="text-xs text-ink-muted font-mono">
                Active deployments across Indian distribution
              </span>
              <a
                href="https://ivmanage.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold text-white bg-plum hover:bg-plum-hover shadow-sm transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-plum"
              >
                Visit ivmanage.com
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Card 2: Zentrak */}
          <div className="flex flex-col justify-between rounded-3xl bg-white p-8 sm:p-10 border border-charcoal/10 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden group">
            {/* Top Accent Bar */}
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-pine" />

            <div>
              {/* Header with Status Badge */}
              <div className="flex items-center justify-between gap-4 pb-6 border-b border-charcoal/5">
                <div className="flex items-center gap-3.5">
                  <div className="p-2.5 rounded-2xl bg-pine/10 text-pine group-hover:scale-105 transition-transform">
                    <ZentrakMark size={36} color="#42333E" />
                  </div>
                  <div>
                    <h3 className="font-brand text-2xl sm:text-3xl font-bold text-ink tracking-tight flex flex-wrap items-baseline gap-x-2">
                      <span>Product 02</span>
                      <span className="text-xs sm:text-sm font-mono font-normal text-ink-muted">
                        [Name Revealed Soon]
                      </span>
                    </h3>
                    <p className="text-xs font-mono text-pine uppercase tracking-wider font-semibold mt-0.5">
                      Field Force Intelligence
                    </p>
                  </div>
                </div>

                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-cream-200 border border-charcoal/15 text-ink-secondary text-xs font-mono font-semibold">
                  <span>COMING SOON</span>
                  <span className="text-charcoal-600 font-normal">
                    · Q4 2026
                  </span>
                </div>
              </div>

              {/* Product Pitch */}
              <div className="pt-6 space-y-4">
                <p className="text-base text-ink leading-relaxed font-medium">
                  Owner-facing command center for distributed field sales,
                  service fleets, and mobile teams.
                </p>
                <p className="text-sm text-ink-secondary leading-relaxed">
                  Engineered from the ground up for continuous background
                  location telemetry, road-snapped route verification, and
                  tamper-resistant shift attendance analytics.
                </p>

                {/* Key Capabilities */}
                <div className="pt-4 space-y-3">
                  <h4 className="text-xs font-mono uppercase tracking-wider text-ink-muted">
                    Key Capabilities
                  </h4>
                  <ul className="space-y-2.5 text-sm text-ink-secondary">
                    <li className="flex items-start gap-2.5">
                      <MapPin className="w-4 h-4 text-pine shrink-0 mt-0.5" />
                      <span>
                        <strong className="text-ink font-semibold">
                          Continuous Location Intelligence:
                        </strong>{" "}
                        Low-power high-frequency trip logging and real-time
                        waypoint streaming.
                      </span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <CheckCircle2 className="w-4 h-4 text-pine shrink-0 mt-0.5" />
                      <span>
                        <strong className="text-ink font-semibold">
                          Geofenced Attendance:
                        </strong>{" "}
                        Geofenced shift punch-in, leave approval flows, and
                        accurate working-hour calculation.
                      </span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <Activity className="w-4 h-4 text-pine shrink-0 mt-0.5" />
                      <span>
                        <strong className="text-ink font-semibold">
                          Route Compliance & Playback:
                        </strong>{" "}
                        Road-matched travel trace playback, stop durations, and
                        beat deviation alerts.
                      </span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <Smartphone className="w-4 h-4 text-pine shrink-0 mt-0.5" />
                      <span>
                        <strong className="text-ink font-semibold">
                          Offline-First Mobile Engine:
                        </strong>{" "}
                        Resilient Flutter mobile framework that queues telemetry
                        across zero-signal pockets.
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Card CTA */}
            <div className="pt-8 mt-8 border-t border-charcoal/5 flex flex-col sm:flex-row items-center justify-between gap-4">
              <span className="text-xs text-ink-muted font-mono">
                Early access preview for enterprise operators
              </span>
              <button
                type="button"
                onClick={onSelectZentrakInquiry}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold text-white bg-pine hover:bg-pine-hover shadow-sm transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-pine"
              >
                Inquire About Early Access
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
